import React, { useEffect, useRef, useState } from "react";
import { Search, CheckCircle2, X, MapPin } from "lucide-react";
import { Constants } from "../../../../utils/constants";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { getTimezoneForCountry } from "../../../../utils/timezoneUtils";

/**
 * Parses a Google Place or Geocoder result into our building address fields.
 * Exported so callers can also use it standalone.
 */
export const parseGooglePlace = place => {
  if (!place) return {};
  const components = {};
  (place.address_components || []).forEach(component => {
    (component.types || []).forEach(type => {
      if (!components[type]) {
        components[type] = component.long_name;
        components[`${type}_short`] = component.short_name;
      }
    });
  });

  const countryComp = (place.address_components || []).find(
    c => Array.isArray(c.types) && c.types.includes("country")
  );

  const streetNumber = components.street_number || "";
  const route = components.route || "";
  const streetAddress = [streetNumber, route].filter(Boolean).join(" ");

  const countryCode = components.country_short || countryComp?.short_name || "";
  const country = components.country || countryComp?.long_name || "";
  const city =
    components.locality ||
    components.sublocality_level_1 ||
    components.administrative_area_level_2 ||
    components.sublocality ||
    "";
  const region = components.administrative_area_level_1 || "";
  const postalCode = components.postal_code || "";
  const timezone = getTimezoneForCountry(countryCode) || "";

  let lat = null;
  let lng = null;
  if (place.geometry?.location) {
    lat = typeof place.geometry.location.lat === "function" ? place.geometry.location.lat() : place.geometry.location.lat;
    lng = typeof place.geometry.location.lng === "function" ? place.geometry.location.lng() : place.geometry.location.lng;
  }

  return {
    buildingName: place.name || streetAddress || "",
    streetAddress: streetAddress || (place.formatted_address ? place.formatted_address.split(",")[0].trim() : ""),
    city,
    region,
    country,
    countryCode,
    postalCode,
    digitalAddress: postalCode,
    timezone,
    latitude: lat,
    longitude: lng,
    formattedAddress: place.formatted_address || "",
  };
};

/**
 * A Google Maps Places Autocomplete search input.
 *
 * Props:
 *  - onPlaceSelected(parsedData)  — called when a place is chosen
 *  - placeholder                  — input placeholder text
 *  - className                    — extra wrapper class
 *  - showSelectedBadge            — whether to render the blue confirmation card (default true)
 */
const GooglePlacesSearchInput = ({
  onPlaceSelected,
  placeholder = "Search your building or address on Google Maps…",
  className = "",
  showSelectedBadge = true,
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const places = useMapsLibrary("places");
  const [isReady, setIsReady] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    if (places) {
      setIsReady(true);
      setLoading(false);
    }
    return () => {
      isMounted.current = false;
    };
  }, [places]);

  /* Attach autocomplete once the library is ready */
  useEffect(() => {
    if (!isReady || !inputRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ["address_components", "geometry", "name", "formatted_address"],
      types: ["establishment", "geocode"],
    });
    autocompleteRef.current = autocomplete;

    const handlePlaceChanged = () => {
      const place = autocomplete.getPlace();
      if (!place?.geometry?.location) return;
      const parsed = parseGooglePlace(place);
      setSelectedPlace(parsed);
      onPlaceSelected(parsed);
    };

    autocomplete.addListener("place_changed", handlePlaceChanged);

    return () => {
      if (window.google?.maps?.event && autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isReady, onPlaceSelected]);

  /* Global interceptor for Google Places dropdown to prevent Radix UI from blocking clicks */
  useEffect(() => {
    const handlePacClick = e => {
      // If the click is inside a pac-container, stop propagation so Radix doesn't see it as a "click outside"
      if (e.target.closest(".pac-container")) {
        e.stopPropagation();
      }
    };

    // Use capture phase to catch events before Radix
    document.addEventListener("mousedown", handlePacClick, true);
    document.addEventListener("pointerdown", handlePacClick, true);
    document.addEventListener("click", handlePacClick, true);

    return () => {
      document.removeEventListener("mousedown", handlePacClick, true);
      document.removeEventListener("pointerdown", handlePacClick, true);
      document.removeEventListener("click", handlePacClick, true);
    };
  }, []);

  const clearSelection = () => {
    setSelectedPlace(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Make sure autocomplete dropdown floats above dialogs */}
      <style>{`
        .pac-container {
          z-index: 99999 !important;
          pointer-events: auto !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 24px rgba(0,0,0,0.13) !important;
          border: 1px solid #e5e7eb !important;
          margin-top: 4px !important;
          font-family: inherit !important;
        }
        .pac-item {
          padding: 8px 12px !important;
          cursor: pointer !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
        }
        .pac-item:hover { background-color: #eff6ff !important; }
        .pac-item-selected { background-color: #dbeafe !important; }
        .pac-matched { font-weight: 600 !important; color: #1d4ed8 !important; }
      `}</style>

      <div className="relative">
        {/* Left icon */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <MapPin className="w-4 h-4 text-blue-500" />
        </div>

        <input
          ref={inputRef}
          type="text"
          placeholder={loading ? "Loading Google Maps…" : placeholder}
          disabled={loading}
          className="w-full pl-10 pr-9 py-3 text-sm border border-blue-200 rounded-xl
                     bg-blue-50/40 placeholder:text-gray-400 text-gray-800
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400
                     transition-all disabled:bg-gray-50 disabled:text-gray-400"
        />

        {/* Spinner while loading */}
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Clear button once a place is selected */}
        {selectedPlace && !loading && (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Error fallback */}
      {error && <p className="text-xs text-amber-600">{error}</p>}

      {/* Confirmation badge */}
      {showSelectedBadge && selectedPlace && (
        <div className="flex items-start gap-2.5 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-medium text-blue-800 block truncate">
              {selectedPlace.buildingName || selectedPlace.formattedAddress}
            </span>
            {selectedPlace.formattedAddress && (
              <p className="text-xs text-blue-500 mt-0.5 truncate">{selectedPlace.formattedAddress}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GooglePlacesSearchInput;
